// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { SegmentKey } from '@/billing/lifecycle/segments';

// Industry benchmark anchors for a single lifecycle segment. All values
// are in calendar days. The three anchors produce a four-zone indicator
// (elite / typical / below-average / concerning) that the summary card's
// pointer maps into.
//
// `signed: true` segments are visualized two-sided (mirrored around 0)
// — the anchors stored here are the absolute magnitudes, and the
// indicator renders ticks at both `−value` and `+value`.
export interface SegmentBenchmark {
  bestInClass: number;
  industryMedian: number;
  concerning: number;
  signed: boolean;
}

// Charge lag (charge eligible → drafted on an invoice).
//
// No B2B platform formally measures this; healthcare RCM is the closest
// analog (the "charge lag" naming itself is borrowed from there). MD
// Clarity and HFMA both cite a 24–48 hour target with a 1–3 day best
// practice and an 8–10 day standard ceiling. Hardfin's auto-flow puts
// the steady-state at ~5 minutes (≈ 0.00 d), so `bestInClass = 1` is
// conservative; `concerning = 10` matches HFMA's "below 8 days" guidance
// with a small buffer.
//
// Sources:
// - https://www.mdclarity.com/rcm-metrics/charge-lag
// - https://www.hfma.org/revenue-cycle/kpis/7-kpis-providers-should-be-tracking/
const CHARGE_LAG_BENCHMARK: SegmentBenchmark = {
  bestInClass: 1,
  industryMedian: 5,
  concerning: 10,
  signed: false,
};

// Invoice review (drafted → opened).
//
// APQC's 2.8 d is the most widely cited best-in-class invoice
// processing benchmark in finance. Ardent Partners' 2024 State of
// ePayables reports a 9.2 d average and a 17 d manual-process median.
// These are AP-side benchmarks (processing inbound invoices), but the
// mechanic — "how long does a finance team take to review an invoice"
// — applies symmetrically on the AR side.
//
// Sources:
// - https://www.apqc.org/resource-library/resource-collection/understanding-accounts-receivable-and-invoicing-benchmarks-and
// - https://www.ascendsoftware.com/blog/what-good-looks-like-ap-benchmarks-every-modern-team-should-know-in-2025
const INVOICE_REVIEW_BENCHMARK: SegmentBenchmark = {
  bestInClass: 2.8,
  industryMedian: 9.2,
  concerning: 17,
  signed: false,
};

// Delivery lag (opened → first sent).
//
// Modern e-invoicing and auto-send platforms (Billtrust, HighRadius,
// Stripe) make same-day delivery the new standard, so we set a 1 d
// best-in-class target. The 2 d median accounts for orgs still mailing
// some invoices manually; 5 d is the threshold beyond which "delivery
// lag" becomes "delivery problem".
//
// Sources:
// - https://www.billtrust.com/resources/solution-guides/billtrust-invoicing
// - https://www.highradius.com/resources/Blog/accounts-payable-benchmarking/
const DELIVERY_LAG_BENCHMARK: SegmentBenchmark = {
  bestInClass: 1,
  industryMedian: 2,
  concerning: 5,
  signed: false,
};

// Date lag (printed invoice date − sent date).
//
// Symmetric magnitudes — both backdated (negative) and future-dated
// (positive) deviate from the ideal of "send on the printed date".
// Quantitative B2B research is thin (most platforms don't measure it),
// so we anchor on the qualitative norm from recurring-billing platform
// docs (Stripe, Chargebee): 1–3 d either direction is routine; beyond
// ±10 d is unusual and worth investigating.
//
// Sources:
// - https://docs.stripe.com/billing/subscriptions/backdating
// - https://www.chargebee.com/docs/billing/2.0/subscriptions/backdating
const DATE_LAG_BENCHMARK: SegmentBenchmark = {
  bestInClass: 1,
  industryMedian: 3,
  concerning: 10,
  signed: true,
};

// Actual terms (invoice date → due date).
//
// This is qualitatively different from the other five — it's a
// function of business model and customer mix, not operational
// efficiency. We render the indicator with the same color treatment
// for visual consistency: green at Net 15 (elite working-capital
// position), red at Net 60+ (extended cycles common in manufacturing
// and heavy equipment). Net 30 is the dominant standard, used by
// roughly 45–60% of B2B transactions.
//
// Sources:
// - https://resolvepay.com/blog/post/net-terms/
// - https://www.creditpulse.com/blog/days-sales-outstanding-dso-by-industry-2025-benchmarks-data-analysis
const ACTUAL_TERMS_BENCHMARK: SegmentBenchmark = {
  bestInClass: 15,
  industryMedian: 30,
  concerning: 60,
  signed: false,
};

// Delinquency (paid date − due date), standard industry DBT.
//
// Experian's "green" zone is ≤15 d with an industry-wide DBT norm of
// 7 d. Esker's AR survey reports a 19 d average DBT. We anchor
// `bestInClass = 5 d` to give the green zone some margin below the
// 7 d norm.
//
// Sources:
// - https://www.businesscreditfacts.com/pdp.aspx?pg=what-is-dbt
// - Esker AR survey (cited in the design doc itself)
const DELINQUENCY_BENCHMARK: SegmentBenchmark = {
  bestInClass: 5,
  industryMedian: 7,
  concerning: 19,
  signed: false,
};

// Indicator-eligible segment keys — the six lifecycle segments. Total
// cycle has no published benchmark and isn't rendered as its own card on
// this page, so it isn't included.
type BenchmarkSegmentKey = Exclude<SegmentKey, 'totalCycle'>;

export const SEGMENT_BENCHMARKS: Record<BenchmarkSegmentKey, SegmentBenchmark> =
  {
    chargeLag: CHARGE_LAG_BENCHMARK,
    invoiceReview: INVOICE_REVIEW_BENCHMARK,
    deliveryLag: DELIVERY_LAG_BENCHMARK,
    dateLag: DATE_LAG_BENCHMARK,
    actualTerms: ACTUAL_TERMS_BENCHMARK,
    delinquency: DELINQUENCY_BENCHMARK,
  };
